import { lazy, Suspense, useState } from 'react';
import { Button, message } from 'antd';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useBalanceTableStore } from 'store/balanceTable';
import { useBalanceTableFiltersStore } from 'store/balanceTableFilters';
import { useMerchantsStore } from 'store/merchants';
import { ExportButton } from 'ui/ExportButton/ExportButton';
import Loader from 'ui/Loader';
import type { TComponent } from 'utils/types/component';

import { useOnce } from 'components/utils/hooks/useOnce';
import { usePageVisibility } from 'components/utils/hooks/usePageVisibility';

import { Filters } from './components/Filters';
import { MerchantCardList } from './components/MerchantCardList';
import { ReconciliationModal } from './components/ReconciliationModal';
import styles from './BalanceTable.module.scss';

const ExportModal = lazy(() => import('./components/ExportModal'));

export const BalanceTable: TComponent = () => {
    const { t } = useTranslation();

    const data = useBalanceTableStore('rows');
    const loading = useBalanceTableStore('isLoading');
    const getMerchants = useMerchantsStore('getMerchants');
    const exportBalances = useBalanceTableStore('exportBalances');

    const body = useBalanceTableFiltersStore('body');

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isReconciliationModalVisible, setIsReconciliationModalVisible] =
        useState(false);

    const handleOpenModal = () => {
        if (body.date) {
            setIsModalVisible(true);
        } else {
            message.error(t('balance.balanceExport.filterOutOfDateError'));
        }
    };

    const handleCloseDetailsModal = () => {
        setIsModalVisible(false);
    };

    const handleExport = (currencyCodes: string[]) => {
        if (body.date) {
            exportBalances({
                ...body,
                currencyCodes,
                date: body.date,
            });
        }
    };

    useOnce(
        () => true,
        () => {
            getMerchants();
        },
        [],
    );

    usePageVisibility(() => {
        getMerchants();
    });

    const handleReconciliationModalOpen = () => {
        setIsReconciliationModalVisible(true);
    };

    const handleReconciliationModalClose = () => {
        setIsReconciliationModalVisible(false);
    };

    return (
        <Loader active={loading}>
            <div className={styles.container}>
                <div className={cn(styles.actionBar, 'actionBar')}>
                    <Filters />
                    <div className={styles.modalOpenersBlock}>
                        <Button
                            className={styles.modalOpenerButton}
                            type='default'
                            onClick={handleReconciliationModalOpen}
                        >
                            {t('balance.balanceTable.reconciliationReport')}
                        </Button>
                        <ExportButton
                            className={styles.modalOpenerButton}
                            onClick={handleOpenModal}
                        />
                    </div>
                </div>
                <MerchantCardList data={data} />

                <ReconciliationModal
                    isOpen={isReconciliationModalVisible}
                    closeModal={handleReconciliationModalClose}
                />

                <Suspense fallback={<Loader active />}>
                    <ExportModal
                        isModalVisible={isModalVisible}
                        onCloseModal={handleCloseDetailsModal}
                        handleExport={handleExport}
                    />
                </Suspense>
            </div>
        </Loader>
    );
};

export default BalanceTable;
